import React, { useState } from 'react';
import OfflineBoltIcon from '@material-ui/icons/OfflineBolt';
import HomeIcon from '@material-ui/icons/Home';
import PersonIcon from '@material-ui/icons/Person';
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import ReplayIcon from '@material-ui/icons/Replay';
import CallSplitIcon from '@material-ui/icons/CallSplit';
import EditIcon from '@material-ui/icons/Edit';
import { ChevronRightIcon, CorrectIcon, LinkCopyIcon } from '../../../../../ui/Icons';
import { FormControlLabel } from '@material-ui/core';
import Select, { ValueType } from 'react-select';
import moment from 'moment';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { ReservationStatus } from '../../../../../shared/enums/reservation-status';
import { ReservationSource } from '../../../../../shared/enums/reservation-souce.enum';
import { BookingMode } from '../../../../../shared/enums/booking-mode.enum';
import { BookOfferResponseModel } from '../../../../../shared/ui-models/book.ui-model';
import { PERMISSIONS_LIST } from '../../../../../shared/config/permissions.config';
import {
  hasAccess,
  hasRole
} from '../../../../../shared/services/helpers/auth-helper/auth-helper.service';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { getBookOffer, reservationSwitchRoom } from '../../../../shared/services/data.service';
import { SwitchRoomModal } from '../switch-room-modal/switch-room-modal.component';
import {
  ReservationSourceChannelModal,
  SourceChannelDraft
} from './reservation-source-channel-modal.component';
import {
  SAHEL_AREAS,
  dropdownItemStatus,
  getReservationStatusColor,
  OrangeSwitch
} from '../../reservation-edit.helper';
import { updateOneReservations } from '../../../../shared/services/data.service';
import Tooltip from '../../../../../ui/Tooltip';
import {
  getReservationChannelImage,
  getReservationChannelLabel,
  getReservationSourceImage,
  getReservationSourceLabel
} from '../../../../../shared/services/helpers/reservation-source-channel.helper';
import { UserRole } from '../../../../../shared/enums/user-role.enum';

export interface ReservationHeaderProps {
  data: {
    reservation: ReservationDetailsUiModel;
    reservationStatus: string | undefined;
    bookofferLoading: boolean;
  };
  eventHandlers: {
    onStatusChange: (newStatus: string) => void;
    onOpenAddCommentModal: () => void;
    onReactivateModalOpen: (bookoffer: BookOfferResponseModel) => void;
    onBookofferLoadingChange: (loading: boolean) => void;
    onRefresh: (id: string) => Promise<any>;
    onOpenSplitReservationModal: () => void;
  };
}

export const ReservationHeader = ({
  data: { reservation, reservationStatus, bookofferLoading },
  eventHandlers: {
    onStatusChange,
    onOpenAddCommentModal,
    onReactivateModalOpen,
    onBookofferLoadingChange,
    onRefresh,
    onOpenSplitReservationModal
  }
}: ReservationHeaderProps) => {
  const renderSourceImage = () => {
    const src = getReservationSourceImage(reservation.source);
    const label = getReservationSourceLabel(reservation.source);

    return <img className="size-8 object-contain" src={src} alt={label} />;
  };

  const renderChannelImage = () => {
    const src = getReservationChannelImage(reservation.channel);
    const label = getReservationChannelLabel(reservation.channel);

    return <img className="size-8 object-contain" src={src} alt={label} />;
  };

  const reservationStatusColourStyles = (stateValue: any) => ({
    singleValue: (styles: any) => ({ ...styles, color: 'inherit' }),
    control: (styles: any) => {
      const colorObj = getReservationStatusColor(stateValue);
      return { ...styles, ...colorObj };
    },
    option: (styles: any, { data }: any) => {
      const colorObj = getReservationStatusColor(data.value);
      return { ...styles, ...colorObj };
    }
  });

  const canReopenCanceledReservation = hasRole([
    UserRole.RESERVATION_MANAGER,
    UserRole.ADMIN,
    UserRole.SUPER_ADMIN
  ]);

  const statusOptions =
    reservation.status === ReservationStatus.CANCELED &&
    canReopenCanceledReservation &&
    reservation.source !== ReservationSource.CHANNEL_MANAGER
      ? [
          ...dropdownItemStatus[reservation.status],
          { label: 'Check in', value: ReservationStatus.CHECKED_IN }
        ]
      : dropdownItemStatus[reservation.status];

  const currency = reservation.paymentList?.[0]?.currency;
  const hasRefund = reservation.refund && reservation.refund.amount > 0;
  const hasCompensation = reservation.compensation && reservation.compensation.amount > 0;
  const hasInsurance = reservation.insuranceCollected && reservation.insuranceCollected.amount > 0;
  const hasChannelCommission =
    reservation.channelCommission && reservation.channelCommission.amount > 0;

  const [showSwitchRoomModal, setShowSwitchRoomModal] = useState(false);
  const [switchRoomLoading, setSwitchRoomLoading] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [showSourceChannelModal, setShowSourceChannelModal] = useState(false);
  const [sourceChannelDraft, setSourceChannelDraft] = useState<SourceChannelDraft>({});
  const [sourceChannelSaving, setSourceChannelSaving] = useState(false);

  const canEditSourceChannel = hasAccess([
    PERMISSIONS_LIST.reservationsFullAccess,
    PERMISSIONS_LIST.reservationsModifyAccess
  ]);
  const saveAttribution = async (changes: SourceChannelDraft) => {
    const { source, channel, salesPerson } = changes;

    setSourceChannelSaving(true);
    const response = await updateOneReservations({
      id: reservation.id,
      source,
      channel,
      salesPerson
    });
    setSourceChannelSaving(false);

    if (isErrorInfo(response)) {
      birdToast.error('Failed to update reservation attribution');
      return false;
    }

    await onRefresh(reservation.id);
    birdToast.success('Reservation attribution updated');
    return true;
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const handleSwitchRoom = (unitTypeId: number, roomId: string) => {
    setSwitchRoomLoading(true);
    reservationSwitchRoom(
      Number(reservation?.id || 0),
      Number(reservation?.unitTypeId || 0),
      unitTypeId,
      roomId
    ).then((response) => {
      setSwitchRoomLoading(false);
      if (!isErrorInfo(response)) {
        birdToast.success('Reservation room switched, and an email is sent to Property Manager(s)');
        onRefresh(String(reservation?.id)).then(() => setShowSwitchRoomModal(false));
      } else {
        if (response.status === 409) {
          birdToast.error(
            'Switch room operation failed, as destination room is not available at the same dates, please contact support for taking further actions'
          );
        } else {
          birdToast.error('Switch room operation failed, Please contact support.');
        }
      }
    });
  };

  return (
    <>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-start gap-4 mb-4 pl-4 pt-4 md:pt-0 md:pl-0 pb-4 border-b border-gray-200">
        <div className="flex flex-col gap-4 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <Tooltip
              title={
                <div className="flex flex-col gap-1 text-base">
                  <span>
                    Created by: {reservation.createdByUsername} on{' '}
                    {moment(reservation.createdAt).format('DD/MM/YYYY HH:mm')}
                  </span>
                  {reservation.modifiedByUsername && (
                    <span>
                      Last modified by: {reservation.modifiedByUsername} on{' '}
                      {moment(reservation.updatedAt).format('DD/MM/YYYY HH:mm')}
                    </span>
                  )}
                </div>
              }
              placement="top"
            >
              <h1 className="text-2xl font-bold text-gray-800 m-0 whitespace-nowrap font-gilroy-medium cursor-help">
                ID: {reservation.id}
              </h1>
            </Tooltip>

            {reservation?.relatedReservationId && (
              <Tooltip title="Open related reservation" placement="top">
                <a
                  href={`${process.env.PUBLIC_URL}/reservations/edit/${reservation.relatedReservationId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-base font-gilroy-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100"
                >
                  Linked to #{reservation.relatedReservationId}
                </a>
              </Tooltip>
            )}

            <Tooltip title={urlCopied ? 'Copied!' : 'Copy link'} placement="top">
              <button
                type="button"
                onClick={handleCopyUrl}
                className="flex items-center justify-center size-7 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-300 text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                aria-label="Copy page URL"
              >
                {urlCopied ? <CorrectIcon /> : <LinkCopyIcon />}
              </button>
            </Tooltip>

            {(reservation.channel || reservation.source) && (
              <>
                <Tooltip
                  title={
                    <span className="text-lg">
                      {getReservationSourceLabel(reservation.source)} →{' '}
                      {getReservationChannelLabel(reservation.channel)}
                      <br />
                      Sales person: {reservation.salesPerson || 'Unassigned'}
                    </span>
                  }
                  placement="top"
                >
                  <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 px-2 py-1 rounded-md cursor-default">
                    {renderSourceImage()}
                    <ChevronRightIcon className="size-3 text-gray-300" />
                    {renderChannelImage()}
                  </div>
                </Tooltip>
                {canEditSourceChannel && (
                  <Tooltip title="Edit source and channel" placement="top">
                    <button
                      type="button"
                      className="flex items-center justify-center size-11 rounded-md border border-gray-200 bg-gray-50 text-gray-500 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 cursor-pointer"
                      onClick={() => {
                        setSourceChannelDraft({
                          source: reservation.source as ReservationSource | undefined,
                          channel: reservation.channel,
                          salesPerson: reservation.salesPerson
                        });
                        setShowSourceChannelModal(true);
                      }}
                      aria-label="Edit reservation source, channel and sales person"
                    >
                      <EditIcon style={{ fontSize: 15 }} />
                    </button>
                  </Tooltip>
                )}
              </>
            )}

            {reservation.bookingMode === BookingMode.INSTANT && (
              <Tooltip title="Instant Booking">
                <div className="flex items-center gap-1 bg-primary-50 text-primary-700 border border-primary-100 px-2 py-1 rounded-md text-xl font-semibold">
                  <OfflineBoltIcon style={{ fontSize: 13 }} />
                  Instant
                </div>
              </Tooltip>
            )}

            {/* Currency */}
            {currency && (
              <Tooltip title={`Currency: ${currency.toUpperCase()}`}>
                <div className="bg-indigo-50 text-indigo-700 border border-indigo-200 px-2 py-1 rounded-md text-xl font-semibold uppercase cursor-default">
                  {currency}
                </div>
              </Tooltip>
            )}
            {reservation?.rateCard &&
              (reservation.rateCard?.length || 0) > 0 &&
              reservation.rateCard[0]?.basePrice &&
              reservation.rateCard[0]?.price &&
              reservation.rateCard[0]?.basePrice !== reservation.rateCard[0]?.price && (
                <Tooltip
                  title={
                    <span>
                      Base price: {reservation.rateCard[0]?.basePrice}
                      <br />
                      Current price: {reservation.rateCard[0]?.price}
                    </span>
                  }
                >
                  <div className="flex items-center gap-1 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-md text-xl font-semibold text-emerald-700 cursor-default">
                    <MonetizationOnIcon style={{ fontSize: 12 }} />
                    {reservation.rateCard[0]?.price}
                  </div>
                </Tooltip>
              )}

            <Tooltip title="Click to assign / switch room">
              <button
                type="button"
                className="flex items-center gap-1 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 px-2 py-1 rounded-md text-xl font-gilroy-medium text-gray-600 hover:text-blue-700 transition-colors cursor-pointer"
                onClick={() => setShowSwitchRoomModal(true)}
              >
                <MeetingRoomIcon style={{ fontSize: 14 }} className="text-neutral-400" />
                <span>{reservation.roomName ? reservation.roomName : 'UNASSIGNED'}</span>
              </button>
            </Tooltip>
            {canReopenCanceledReservation && (
              <Tooltip title="Shorten Reservation">
                <button
                  type="button"
                  className="flex items-center gap-1 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-300 px-2 py-1 rounded-md text-xl font-gilroy-medium text-gray-600 hover:text-blue-700 transition-colors cursor-pointer"
                  onClick={onOpenSplitReservationModal}
                >
                  <CallSplitIcon style={{ fontSize: 14 }} className="text-neutral-400" />
                  <span>Shorten Reservation</span>
                </button>
              </Tooltip>
            )}

            {(hasRefund || hasCompensation || hasInsurance || hasChannelCommission) && (
              <Tooltip
                title={
                  <div className="flex flex-col gap-1">
                    {hasRefund && (
                      <span>
                        Refund: {reservation.refund!.amount}{' '}
                        {reservation.refund!.currency?.toUpperCase()}
                      </span>
                    )}
                    {hasCompensation && (
                      <span>
                        Compensation: {reservation.compensation!.amount}{' '}
                        {reservation.compensation!.currency?.toUpperCase()}
                      </span>
                    )}
                    {hasInsurance && (
                      <span>
                        Insurance: {reservation.insuranceCollected!.amount}{' '}
                        {reservation.insuranceCollected!.currency?.toUpperCase()}
                      </span>
                    )}
                    {hasChannelCommission && (
                      <span>
                        Channel Commission: {reservation.channelCommission!.amount}{' '}
                        {reservation.channelCommission!.currency?.toUpperCase()}
                      </span>
                    )}
                  </div>
                }
              >
                <div className="flex items-center gap-1 bg-orange-50 text-orange-700 border border-orange-200 px-2 py-1 rounded-md text-xl font-semibold cursor-default">
                  <MonetizationOnIcon style={{ fontSize: 12 }} />
                  Financials
                </div>
              </Tooltip>
            )}
          </div>

          {/* Row 2: property / unit / room / area / guest */}
          <div className="flex items-center gap-3 flex-wrap text-sm text-gray-600">
            {reservation.area && (
              <>
                <span className="text-gray-300">·</span>
                <Tooltip title="Area">
                  <span className="flex items-center gap-1">
                    <LocationOnIcon style={{ fontSize: 14 }} className="text-neutral-400 " />
                    <span className="text-xl font-gilroy-medium">{reservation.area}</span>
                  </span>
                </Tooltip>
              </>
            )}
            {reservation.propertyName && (
              <Tooltip title="Property">
                <a
                  href={`${process.env.PUBLIC_URL}/properties/edit/${reservation.propertyId}`}
                  className="cursor-pointer"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="flex items-center gap-1">
                    <HomeIcon style={{ fontSize: 14 }} className="text-neutral-400 " />
                    <span className="text-xl font-gilroy-medium">{reservation.propertyName}</span>
                  </span>
                </a>
              </Tooltip>
            )}
            {reservation.unitName && (
              <>
                <span className="text-gray-300">·</span>
                <Tooltip title="Unit">
                  <a
                    href={`${process.env.PUBLIC_URL}/unit-types/edit/${reservation.unitTypeId}`}
                    className="cursor-pointer"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className="text-xl font-gilroy-medium">{reservation.unitName}</span>
                  </a>
                </Tooltip>
              </>
            )}
            {reservation.guestIds && reservation.guestIds.length > 0 && (
              <>
                <Tooltip title="Guests">
                  <span className="flex items-center gap-1">
                    <PersonIcon style={{ fontSize: 14 }} className="text-neutral-400 " />
                    <span className="text-xl font-gilroy-medium">
                      {reservation.guestIds.length} Guests
                    </span>
                  </span>
                </Tooltip>
              </>
            )}
          </div>
        </div>

        {/* Right: Sahel validation + reactivate + status dropdown */}
        <div className="flex items-center gap-3 flex-wrap shrink-0">
          {reservation.areaId &&
            SAHEL_AREAS?.includes(reservation.areaId) &&
            (reservation.status === ReservationStatus.CHECKED_IN ||
              reservation.status === ReservationStatus.PENDING_CHECK_IN) && (
              <div className="bg-orange-50 px-3 py-1.5 rounded-lg border border-orange-100">
                <FormControlLabel
                  control={
                    <OrangeSwitch
                      checked={reservation.isValidated}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        updateOneReservations({
                          id: reservation.id,
                          isValidated: event.target.checked
                        }).then(() => onRefresh(reservation.id));
                      }}
                      name="checked"
                      color="primary"
                    />
                  }
                  label={
                    <span className="text-lg font-gilroy-medium">
                      {reservation.isValidated ? 'Validated' : 'Not Validated'}
                    </span>
                  }
                />
              </div>
            )}

          {reservation.status === ReservationStatus.EXPIRED && (
            <Button
              type={ButtonType.danger}
              className="!rounded-lg shadow-sm"
              onClick={() => {
                onBookofferLoadingChange(true);
                getBookOffer(
                  reservation.unitTypeId || '',
                  moment(reservation.checkInDate).format('DD/MM/YYYY'),
                  moment(reservation.checkOutDate).format('DD/MM/YYYY')
                ).then((res) => {
                  onBookofferLoadingChange(false);
                  if (!isErrorInfo(res)) {
                    if (
                      (res as BookOfferResponseModel).availableRooms &&
                      (res as BookOfferResponseModel).availableRooms.length > 0
                    ) {
                      onReactivateModalOpen(res as BookOfferResponseModel);
                    } else {
                      birdToast.error('No available rooms to reactivate reservation');
                    }
                  } else {
                    birdToast.error('No available rooms to reactivate reservation');
                  }
                });
              }}
              loading={bookofferLoading}
              disabled={reservation.status !== ReservationStatus.EXPIRED}
              size={ButtonSize.small}
            >
              <div className="flex items-center gap-1.5">
                <ReplayIcon style={{ fontSize: 16 }} />
                <span className="font-semibold">Reactivate</span>
              </div>
            </Button>
          )}

          <div className="flex flex-col gap-1 w-52">
            <Select
              isDisabled={
                reservation.source === ReservationSource.CHANNEL_MANAGER ||
                !hasAccess([
                  PERMISSIONS_LIST.reservationsFullAccess,
                  PERMISSIONS_LIST.reservationsModifyAccess
                ])
              }
              styles={reservationStatusColourStyles(reservationStatus)}
              options={statusOptions}
              defaultValue={statusOptions?.[0]}
              onChange={(value: ValueType<{ value: ReservationStatus; label: string }, false>) => {
                onOpenAddCommentModal();
                onStatusChange(value ? value.value : '');
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
            {reservation.source === ReservationSource.CHANNEL_MANAGER && (
              <span className="text-[11px] text-red-500 px-1">
                Updates only on{' '}
                {reservation.channel ? reservation.channel.toUpperCase() : 'OTA directly'}
              </span>
            )}
          </div>
        </div>
      </div>

      <SwitchRoomModal
        config={{ showModal: showSwitchRoomModal }}
        data={{ reservation, switchRoomLoading }}
        eventHandlers={{
          closeHandler: () => setShowSwitchRoomModal(false),
          switchRoomHandler: handleSwitchRoom
        }}
      />

      <ReservationSourceChannelModal
        data={{
          reservation,
          showModal: showSourceChannelModal,
          sourceChannelDraft,
          sourceChannelSaving,
          disableSourceChannelFields: reservation.source === ReservationSource.CHANNEL_MANAGER
        }}
        eventHandlers={{
          onClose: () => setShowSourceChannelModal(false),
          onDraftChange: setSourceChannelDraft,
          onSave: async () => {
            const saved = await saveAttribution(sourceChannelDraft);
            if (saved) setShowSourceChannelModal(false);
          }
        }}
      />
    </>
  );
};
